import { inRange } from "es-toolkit/compat";
import { UIEvent, useCallback, useMemo, useRef } from "react";
import {
  PERCENTAGE_EXCLUSIVE_END,
  PERCENTAGE_MAX,
} from "@/constants/percentage.constants";
import { useTrack } from "@/hooks/useTrack/useTrack";
import { ScrollDepthReached } from "@/hooks/useTrack/useTrack.decl";
import { useAutoScrollState } from "../useAutoScrollState/useAutoScrollState";
import { useCall } from "../useCall/useCall";
import { TIMEOUT_DELAY } from "./useTrackScrollDepth.constants";

/** Lower bound of each tracked scroll-depth band, in percent. */
const SCROLL_DEPTH_QUARTER = 25;
const SCROLL_DEPTH_HALF = 50;
const SCROLL_DEPTH_THREE_QUARTERS = 75;
/** Bands start 5 points before the tracked milestone so a brief pause still counts. */
const SCROLL_DEPTH_BAND_LEAD = 5;

const ranges = [
  [SCROLL_DEPTH_QUARTER - SCROLL_DEPTH_BAND_LEAD, SCROLL_DEPTH_QUARTER],
  [SCROLL_DEPTH_HALF - SCROLL_DEPTH_BAND_LEAD, SCROLL_DEPTH_HALF],
  [
    SCROLL_DEPTH_THREE_QUARTERS - SCROLL_DEPTH_BAND_LEAD,
    SCROLL_DEPTH_THREE_QUARTERS,
  ],
  // `inRange` is exclusive on the end value, so the last band ends one past 100.
  [PERCENTAGE_MAX - SCROLL_DEPTH_BAND_LEAD, PERCENTAGE_EXCLUSIVE_END],
];

export function useTrackScrollDepth() {
  const { track } = useTrack();

  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenRanges = useRef<Set<number>>(new Set());
  const hasScrolled = useRef<boolean>(false);

  const { call } = useCall();
  const { autoScrollEnabled } = useAutoScrollState();

  const trackScrollDepth = useCallback(
    (depth: ScrollDepthReached["scroll_depth"]) => () => {
      track("scroll_depth_reached", {
        call_id: Number(call?.id),
        call_uuid: call?.uuid,
        scroll_depth: depth,
      });
    },
    [call?.id, call?.uuid, track]
  );
  const trackCallbacks = useMemo(
    () => [
      trackScrollDepth("25%"),
      trackScrollDepth("50%"),
      trackScrollDepth("75%"),
      trackScrollDepth("100%"),
    ],
    [trackScrollDepth]
  );

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!hasScrolled.current) {
      hasScrolled.current = true;
      track("transcript_scroll_started", {
        call_id: Number(call?.id),
        call_uuid: call?.uuid,
      });
    }

    // Do not do anything if autoscroll is enabled.
    if (autoScrollEnabled) {
      return;
    }

    // The user already scrolled, so we can clear the timeout.
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // We have to assert here.
    const event = e.target as HTMLDivElement;
    const { scrollTop, clientHeight, scrollHeight } = event;

    const percentageScrolled = Math.round(
      ((scrollTop + clientHeight) / scrollHeight) * PERCENTAGE_MAX
    );

    const callbackIndex = ranges.findIndex(([start, end]) =>
      inRange(percentageScrolled, start, end)
    );

    // Do not call any callback if you did not find one, or if you have already seen it.
    if (callbackIndex < 0 || seenRanges.current.has(callbackIndex)) {
      return;
    }

    timeoutId.current = setTimeout(() => {
      seenRanges.current.add(callbackIndex);
      trackCallbacks[callbackIndex]();
    }, TIMEOUT_DELAY);
  };

  return { onScroll };
}
